import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase';

const getTaskEntriesForCategory = (categoryData = {}) => {
  if (!categoryData || typeof categoryData !== 'object') return [];

  if (categoryData.workflow?.tasks && typeof categoryData.workflow.tasks === 'object') {
    return Object.entries(categoryData.workflow.tasks).filter(([, task]) => task && typeof task === 'object');
  }

  return Object.entries(categoryData).filter(([key, task]) => (
    key !== 'workflow' && task && typeof task === 'object'
  ));
};

const ORDERED_CATEGORIES = [
  'Before Day One',
  'Day One',
  'First Week',
  'First Month',
  '90-Day Review'
];

const Onboarding = () => {
  const { employeeContext } = useAuth();
  const { companyId, employeeId } = employeeContext || {};
  const [tasks, setTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    if (!companyId || !employeeId) return;

    const tasksRef = ref(database, `companies/${companyId}/onboarding/${employeeId}/tasks`);
    const unsubscribe = onValue(tasksRef, (snap) => {
      const val = snap.val() || {};
      setTasks(val);
      
      // Default expand the first category that has incomplete tasks
      const allCats = [
        ...ORDERED_CATEGORIES,
        ...Object.keys(val).filter((cat) => !ORDERED_CATEGORIES.includes(cat))
      ];
      const initialExpanded = {};
      let foundActive = false;
      
      allCats.forEach(cat => {
        if (!val[cat]) return;
        if (!foundActive) {
            const hasIncomplete = getTaskEntriesForCategory(val[cat]).some(([, task]) => task?.status !== 'completed');
            if (hasIncomplete) {
                initialExpanded[cat] = true;
                foundActive = true;
            }
        }
      });
      // If everything completed or empty, expand first available
      if (!foundActive) {
        const first = allCats.find(c => val[c]);
        if (first) initialExpanded[first] = true;
      }
      
      setExpandedCategories(initialExpanded);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [companyId, employeeId]);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
        ...prev,
        [category]: !prev[category]
    }));
  };

  const calculateProgress = () => {
    let total = 0;
    let completed = 0;
    Object.values(tasks).forEach(category => {
      getTaskEntriesForCategory(category).forEach(([, task]) => {
        total++;
        if (task.status === 'completed') completed++;
      });
    });
    return total === 0 ? 0 : Math.round((completed / total) * 100);
  };

  const calculateCategoryProgress = (categoryTasks) => {
    const tasksArr = getTaskEntriesForCategory(categoryTasks).map(([, task]) => task);
    if (tasksArr.length === 0) return 0;
    const completed = tasksArr.filter(t => t.status === 'completed').length;
    return Math.round((completed / tasksArr.length) * 100);
  };

  if (loading) return <div className="loading-state"><i className="fa-solid fa-spinner fa-spin"></i> Loading tasks...</div>;

  const totalProgress = calculateProgress();
  const categoriesToRender = [
    ...ORDERED_CATEGORIES.filter((category) => Boolean(tasks[category])),
    ...Object.keys(tasks).filter((category) => !ORDERED_CATEGORIES.includes(category))
  ];

  return (
    <div className="onboarding-container">
      <div className="onboarding-header">
        <div>
            <h1 className="page-title">Onboarding Journey</h1>
            <p className="page-subtitle">Complete your tasks to settle in.</p>
        </div>
        <div className="progress-card">
            <div className="progress-circle">
                <svg viewBox="0 0 36 36" className="circular-chart">
                    <path className="circle-bg"
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path className="circle"
                        strokeDasharray={`${totalProgress}, 100`}
                        d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                </svg>
                <div className="percentage">{totalProgress}%</div>
            </div>
            <span className="progress-label">Overall Completion</span>
        </div>
      </div>

      {Object.keys(tasks).length === 0 ? (
        <div className="empty-state">
          <i className="fa-solid fa-clipboard-check"></i>
          <p>No onboarding tasks assigned yet.</p>
        </div>
      ) : (
        <div className="timeline-view">
            {categoriesToRender.map((category, index) => {
                const categoryTasks = tasks[category];
                if (!categoryTasks) return null;
                const categoryTaskEntries = getTaskEntriesForCategory(categoryTasks);
                if (!categoryTaskEntries.length) return null;
                
                const isExpanded = expandedCategories[category];
                const catProgress = calculateCategoryProgress(categoryTasks);
                const isCompleted = catProgress === 100;
                
                return (
                    <div key={category} className={`timeline-group ${isCompleted ? 'completed-group' : ''}`}>
                        <div className="timeline-marker">
                            <div className={`marker-dot ${isCompleted ? 'completed' : ''}`}>
                                {isCompleted ? <i className="fa-solid fa-check"></i> : (index + 1)}
                            </div>
                            {index !== categoriesToRender.length - 1 && <div className="marker-line"></div>}
                        </div>
                        
                        <div className="group-content">
                            <div 
                                className={`category-header ${isExpanded ? 'expanded' : ''}`}
                                onClick={() => toggleCategory(category)}
                            >
                                <div className="cat-title-section">
                                    <h3>{category}</h3>
                                    <div className="cat-meta">
                                        {categoryTaskEntries.length} Tasks • {catProgress}% Done
                                    </div>
                                </div>
                                <i className={`fa-solid fa-chevron-down ${isExpanded ? 'rotate' : ''}`}></i>
                            </div>

                            {isExpanded && (
                                <div className="tasks-list">
                                    {categoryTaskEntries.map(([taskId, task]) => {
                                        const taskTitle = String(task?.title || '');
                                        const lowerTaskTitle = taskTitle.toLowerCase();
                                        const isDocumentTask = task?.type === 'document'
                                          || task?.automationType === 'document_request'
                                          || lowerTaskTitle.includes('upload')
                                          || (Array.isArray(task?.requiredDocuments) && task.requiredDocuments.length > 0);
                                        return (
                                        <div 
                                            key={taskId} 
                                            className={`task-card ${task.status === 'completed' ? 'completed' : ''}`}
                                        >
                                            <div className="task-status-icon">
                                                {task.status === 'completed' ? (
                                                    <i className="fa-solid fa-circle-check" style={{color: '#00C851'}}></i>
                                                ) : (
                                                    <i className="fa-regular fa-circle" style={{color: 'rgba(255,255,255,0.3)'}}></i>
                                                )}
                                            </div>
                                            <div className="task-details">
                                                <h4 className="task-title">{taskTitle || 'Untitled task'}</h4>
                                                {task.description && (
                                                    <p className="task-desc">{task.description}</p>
                                                )}
                                                {task.dueDate && (
                                                    <div className="task-due">
                                                        <i className="fa-solid fa-clock"></i> Due: {new Date(task.dueDate).toLocaleDateString()}
                                                    </div>
                                                )}
                                                {isDocumentTask && (
                                                    <div className="task-action-hint">
                                                        <i className="fa-solid fa-arrow-right"></i> Go to Documents to upload
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )})}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
      )}

      <style>{`
        .onboarding-container {
            padding: 20px;
            max-width: 900px;
            margin: 0 auto;
        }
        .onboarding-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 40px;
        }
        .page-title {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 8px;
            font-family: 'Montserrat', sans-serif;
        }
        .page-subtitle {
            color: rgba(255,255,255,0.7);
            font-family: 'Montserrat', sans-serif;
        }
        
        .progress-card {
            display: flex;
            align-items: center;
            gap: 15px;
            background: rgba(86, 141, 237, 0.1);
            padding: 10px 20px;
            border-radius: 50px;
            border: 1px solid rgba(86, 141, 237, 0.2);
        }
        .progress-circle {
            position: relative;
            width: 40px;
            height: 40px;
        }
        .circular-chart {
            display: block;
            margin: 0 auto;
            max-width: 100%;
            max-height: 100%;
        }
        .circle-bg {
            fill: none;
            stroke: rgba(255,255,255,0.1);
            stroke-width: 3.8;
        }
        .circle {
            fill: none;
            stroke: #00C851;
            stroke-width: 2.8;
            stroke-linecap: round;
            transition: stroke-dasharray 0.3s ease;
        }
        .percentage {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 10px;
            font-weight: bold;
        }
        .progress-label {
            font-size: 14px;
            font-weight: 600;
        }

        .timeline-view {
            display: flex;
            flex-direction: column;
        }
        .timeline-group {
            display: flex;
            gap: 20px;
            padding-bottom: 20px;
        }
        .timeline-marker {
            display: flex;
            flex-direction: column;
            align-items: center;
            min-width: 40px;
        }
        .marker-dot {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: rgba(255,255,255,0.1);
            border: 2px solid rgba(255,255,255,0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 14px;
            color: rgba(255,255,255,0.7);
            margin-bottom: 10px;
            z-index: 2;
        }
        .marker-dot.completed {
            background: #00C851;
            border-color: #00C851;
            color: white;
        }
        .marker-line {
            flex: 1;
            width: 2px;
            background: rgba(255,255,255,0.1);
        }
        .timeline-group:last-child .marker-line {
            display: none;
        }

        .group-content {
            flex: 1;
        }
        .category-header {
            background: rgba(86, 141, 237, 0.08);
            border: 1px solid rgba(86, 141, 237, 0.15);
            border-radius: 12px;
            padding: 16px 20px;
            cursor: pointer;
            display: flex;
            justify-content: space-between;
            align-items: center;
            transition: all 0.2s;
            margin-bottom: 12px;
        }
        .category-header:hover {
            background: rgba(86, 141, 237, 0.15);
        }
        .cat-title-section h3 {
            font-size: 16px;
            font-weight: 600;
            margin: 0 0 4px 0;
            font-family: 'Montserrat', sans-serif;
        }
        .cat-meta {
            font-size: 12px;
            color: rgba(255,255,255,0.5);
        }
        .fa-chevron-down {
            transition: transform 0.3s;
        }
        .fa-chevron-down.rotate {
            transform: rotate(180deg);
        }

        .tasks-list {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding-left: 10px;
            animation: slideDown 0.3s ease-out;
        }
        @keyframes slideDown {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        .task-card {
            background: rgba(0,0,0,0.2);
            border-radius: 8px;
            padding: 16px;
            display: flex;
            gap: 16px;
            border: 1px solid transparent;
            transition: all 0.2s;
        }
        .task-card.completed {
            opacity: 0.6;
        }
        
        .task-status-icon {
            font-size: 18px;
            padding-top: 2px;
        }

        .task-details {
            flex: 1;
        }
        .task-title {
            font-size: 15px;
            font-weight: 500;
            margin: 0 0 4px 0;
            font-family: 'Montserrat', sans-serif;
        }
        .task-card.completed .task-title {
            text-decoration: line-through;
            color: rgba(255,255,255,0.5);
        }
        .task-desc {
            font-size: 13px;
            color: rgba(255,255,255,0.6);
            margin: 0 0 8px 0;
            line-height: 1.4;
        }
        .task-due {
            font-size: 11px;
            color: #ffbb33;
            display: flex;
            align-items: center;
            gap: 6px;
        }
        .task-action-hint {
            font-size: 11px;
            color: #568ded;
            margin-top: 8px;
            font-weight: 500;
        }
        
        .loading-state, .empty-state {
            text-align: center;
            padding: 60px;
            color: rgba(255,255,255,0.5);
        }
        .empty-state i {
            font-size: 48px;
            margin-bottom: 20px;
            opacity: 0.5;
        }
      `}</style>
    </div>
  );
};

export default Onboarding;
